import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";
import { db } from "../lib/firebase";
import {
  toRealtimeLoadError,
  type RealtimeLoadError,
} from "../lib/realtimeRecovery";
import type {
  AccountType,
  IdentityEntitlements,
  LegacyAccountType,
  VerificationStatus,
} from "../types/identity";

export type CommentItem = {
  id: string;
  authorUid: string;
  authorUsername?: string;
  /** @deprecated Compatibility snapshot for historical student/teacher comments. */
  authorRole?: LegacyAccountType;
  authorAccountType?: AccountType;
  authorVerificationStatus?: VerificationStatus;
  authorEntitlements?: IdentityEntitlements;
  /** @deprecated Compatibility projection for legacy teacher comments. */
  teacherVerified?: boolean;
  text: string;
  createdAt: any;
};

export function useComments(postId?: string, blockedAuthorIds?: Set<string>) {
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [commentsError, setCommentsError] = useState<RealtimeLoadError | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const previousPostIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const postChanged = previousPostIdRef.current !== postId;
    previousPostIdRef.current = postId;

    if (postChanged) setComments([]);
    setCommentsError(null);
    setLoadingComments(true);

    if (!postId) {
      setLoadingComments(false);
      return;
    }

    let active = true;

    const q = query(
      collection(db, "posts", postId, "comments"),
      orderBy("createdAt", "asc"),
      limit(100)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        if (!active) return;
        const list: CommentItem[] = snap.docs.map((d) => {
          const data = d.data() as any;
          return {
            id: d.id,
            authorUid: data.authorUid,
            authorUsername: data.authorUsername,
            authorRole: data.authorRole,
            authorAccountType: data.authorAccountType,
            authorVerificationStatus: data.authorVerificationStatus,
            authorEntitlements: data.authorEntitlements,
            teacherVerified: data.teacherVerified,
            text: data.text ?? "",
            createdAt: data.createdAt,
          };
        });

        setComments(
          blockedAuthorIds
            ? list.filter((item) => !blockedAuthorIds.has(item.authorUid))
            : list
        );
        setCommentsError(null);
        setLoadingComments(false);
      },
      (error) => {
        if (!active) return;
        active = false;
        if (__DEV__) console.warn("[COMMENTS] snapshot error:", error?.code ?? "unknown");
        setCommentsError(
          toRealtimeLoadError(error, "Impossible de charger les commentaires.")
        );
        setLoadingComments(false);
      }
    );

    return () => {
      active = false;
      unsub();
    };
  }, [blockedAuthorIds, postId, retryKey]);

  const retryComments = useCallback(() => {
    setCommentsError(null);
    setLoadingComments(true);
    setRetryKey((key) => key + 1);
  }, []);

  return { comments, loadingComments, commentsError, retryComments };
}
